import React, { useState } from "react";
import { AnyObject, Form, FormSpy } from "react-final-form";
import { useSelector } from "react-redux";
import { AddBox as AddBoxIcon } from "@mui/icons-material";
import { Box, Button, Grid, SelectChangeEvent, TableContainer, Theme } from "@mui/material";
import { makeStyles } from "@mui/styles";
import classNames from "classnames";
import { useSnackbar } from "notistack";

import { LoadingButton } from "ui";

import { selectVersionsByProjectId, useCreateExportMutation, useGetLayersQuery } from "fond/api";
import { useCurrentProject } from "fond/hooks/useCurrentProject";
import { Store, Version } from "fond/types";
import { required } from "fond/utils/validation";
import { Message as MessageWidget, Modal } from "fond/widgets";

import LayerTree from "../LayerTree";

import { LayerBox, StyledSelectField, StyledTextField } from "../NewExportModal/NewExportModal.styles";

const useCustomStyles = makeStyles((theme: Theme) => ({
  selectLabel: {
    fontSize: theme.typography.size.normal,
    marginBottom: 4,
  },
  textLabel: {
    marginBottom: 4,
  },
}));

interface IFormData {
  description: string;
  name: string;
  version: string;
}

interface NewConstructionPrintsModalProps {
  "data-testid"?: string;
  nativeSelect?: boolean;
  onClose(): void;
  onPostExport?(): void;
}

const NewConstructionPrintsModal: React.FC<NewConstructionPrintsModalProps> = ({
  "data-testid": dataTestid = "new-construction-prints-modal",
  nativeSelect = false,
  onClose,
  onPostExport,
}: NewConstructionPrintsModalProps) => {
  const classes = useCustomStyles();
  const { enqueueSnackbar } = useSnackbar();
  const project = useCurrentProject();

  const versions = useSelector((state: Store): Version[] => (project && selectVersionsByProjectId(state, project.ID)) || []);
  const currentVersionId = useSelector((state: Store) => state.project.versionId);
  const versionOptions = versions.map((item) => ({ value: item.ID, displayValue: item.Name }));
  const showVersionSelect = versions.length > 1;

  // Default to the latest version (versions[0]) when the project is currently being viewed at the latest version;
  // otherwise leave the field blank so the user picks explicitly.
  const latestVersion = versions[0];
  const isOnLatestVersion = !!currentVersionId && latestVersion?.ID === currentVersionId;
  const initialVersion = !showVersionSelect ? latestVersion : isOnLatestVersion ? latestVersion : undefined;

  const [selectedVersion, setSelectedVersion] = useState<Version | undefined>(initialVersion);
  const [selectedLayers, setSelectedLayers] = useState<string[]>([]);
  const [createExport, { isLoading: isSaving, isError }] = useCreateExportMutation();

  useGetLayersQuery(selectedVersion?.ID || "", {
    skip: !selectedVersion?.ID,
    refetchOnMountOrArgChange: true,
  });

  let submit: (event?: Partial<Pick<React.SyntheticEvent, "preventDefault" | "stopPropagation">>) => Promise<AnyObject | undefined> | undefined;

  const handleOnClick = () => {
    submit();
  };

  const onSubmit = async (values: IFormData) => {
    const params = {
      VersionID: values.version || versions[0].ID,
      Name: values.name,
      Format: "shp",
      Description: values.description,
      SystemOfMeasurement: project.SystemOfMeasurement,
      BOM: false,
      SpliceTables: false,
      FiberCableSplit: false,
      HubSplit: false,
      SpliceSplit: false,
      Layers: selectedLayers,
      ConstructionPrints: true,
    };

    await createExport(params)
      .unwrap()
      .then(() => {
        onClose();
        onPostExport?.();
        enqueueSnackbar(`Created construction prints '${values.name}'.`, { variant: "success" });
      });
  };

  const handleVersionSelect = (event: SelectChangeEvent) => {
    if (event.target.value) {
      const newVersionId = event.target.value;
      setSelectedVersion(versions.find((item) => item.ID === newVersionId));
      setSelectedLayers([]);
    }
  };

  return (
    <Form<IFormData>
      initialValues={{ name: "Construction Prints", description: "", version: initialVersion?.ID ?? "" }}
      onSubmit={onSubmit}
      render={({ handleSubmit }) => {
        submit = handleSubmit;
        return (
          <Modal
            open
            onClose={onClose}
            header="New Construction Prints"
            headerIcon={<AddBoxIcon />}
            data-testid={dataTestid}
            variant="primary"
            content={
              <Box>
                {isError && (
                  <Box mb={2} data-testid="construction-prints-error">
                    <MessageWidget type="error">There was an issue creating the construction prints. Please try again.</MessageWidget>
                  </Box>
                )}
                <form onSubmit={handleSubmit}>
                  <Grid container display="flex" alignItems="flex-start" justifyContent="space-between" spacing={2}>
                    <Grid item xs={6}>
                      {showVersionSelect && (
                        <StyledSelectField
                          name="version"
                          labelId="version"
                          label="Select Version"
                          data-testid="construction-prints-version-select"
                          required
                          hideError
                          validate={required}
                          sxProps={{ minWidth: 160 }}
                          nativeSelect={nativeSelect}
                          options={versionOptions}
                          labelClasses={{ root: classes.selectLabel }}
                          onCustomChange={handleVersionSelect}
                        />
                      )}
                      <Box pt={2}>
                        <StyledTextField
                          name="name"
                          inputProps={{ "data-testid": "construction-prints-name" }}
                          required
                          hideError
                          fullWidth
                          label="Name"
                          margin="dense"
                          validate={required}
                          labelClasses={{ root: classes.textLabel }}
                          placeholder="Construction Print Name"
                          sx={{ marigin: 0 }}
                        />
                      </Box>
                      <Box pt={2}>
                        <StyledTextField
                          inputProps={{ "data-testid": "construction-prints-description" }}
                          labelClasses={{ root: classes.textLabel }}
                          style={{ marginTop: 0 }}
                          name="description"
                          label="Description (Optional)"
                          placeholder="Enter description"
                        />
                      </Box>
                    </Grid>
                    <Grid item xs={6} pt={1}>
                      <LayerBox>
                        <TableContainer className={classNames("customScrollbars")} sx={{ height: 400, overflow: "auto" }}>
                          <LayerTree
                            versionId={selectedVersion?.ID}
                            onSelectionChange={setSelectedLayers}
                            excludedKeys={["projectComments"]}
                            title={selectedVersion?.Name}
                            showIcons
                          />
                        </TableContainer>
                      </LayerBox>
                    </Grid>
                  </Grid>
                </form>
              </Box>
            }
            actions={
              <>
                <Button data-testid="cancel-button" onClick={onClose}>
                  Cancel
                </Button>
                <FormSpy<IFormData> subscription={{ values: true }}>
                  {() => (
                    <LoadingButton
                      data-testid="construction-prints-submit"
                      variant="contained"
                      color="primary"
                      onClick={handleOnClick}
                      loading={isSaving}
                      disabled={selectedLayers.length === 0}
                    >
                      Create
                    </LoadingButton>
                  )}
                </FormSpy>
              </>
            }
          />
        );
      }}
    />
  );
};

export default NewConstructionPrintsModal;
